/**
 * useSeo Hook
 * 
 * Custom hook for managing SEO metadata with i18n support.
 * Loads SEO configuration from locale files and provides utilities
 * for generating meta tags, Open Graph, Twitter Cards, and JSON-LD schema.
 * 
 * Features:
 * - i18n support for SEO content
 * - Dynamic meta tag generation
 * - Open Graph and Twitter Card support
 * - JSON-LD schema generation
 * - Platform-specific SEO optimization
 */

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { platformSlugs } from '@/config/platformSlugs';

export interface SeoConfig {
  title: string;
  description: string;
  keywords: string;
  image: string;
  url: string;
  schema?: any;
  robots?: string;
}

export interface SeoMetaTags {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  openGraph: {
    title: string;
    description: string;
    image: string;
    url: string;
    type: string;
    siteName: string;
    locale: string;
  };
  twitter: {
    card: string;
    title: string;
    description: string;
    image: string;
    site: string;
  };
  additionalTags: Array<{
    name: string;
    content: string;
  }>;
  jsonLd?: any;
  hreflangs?: Array<{
    hreflang: string;
    href: string;
  }>;
}

export function useSeo(pageKey?: string): {
  seoConfig: SeoConfig;
  metaTags: SeoMetaTags;
  generateMetaTags: (customConfig?: Partial<SeoConfig>) => SeoMetaTags;
} {
  const { t } = useTranslation('seo');
  const router = useRouter();
  const { locale = 'en' } = router;

  // Dynamically import seo-templates.json based on locale
  const seoTemplates = useMemo(() => {
    try {
      if (locale === 'vi') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return require('../../../public/locales/vi/seo-templates.json');
      }
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      return require('../../../public/locales/en/seo-templates.json');
    } catch (e) {
      return {};
    }
  }, [locale]);

  // Get default SEO configuration
  const defaultSeo = useMemo(() => {
    try {
      return {
        siteName: t('defaultSeo.siteName'),
        siteUrl: t('defaultSeo.siteUrl'),
        defaultTitle: t('defaultSeo.defaultTitle'),
        defaultDescription: t('defaultSeo.defaultDescription'),
        defaultKeywords: t('defaultSeo.defaultKeywords'),
        defaultImage: t('defaultSeo.defaultImage'),
        twitterHandle: t('defaultSeo.twitterHandle'),
        themeColor: t('defaultSeo.themeColor'),
        language: t('defaultSeo.language'),
        locale: t('defaultSeo.locale')
      };
    } catch (error) {
      // Fallback if translation fails
      return {
        siteName: 'DownloadMod',
        siteUrl: 'https://downloadmod.com',
        defaultTitle: 'DownloadMod - Free Video Downloader',
        defaultDescription: 'Download videos from multiple platforms for free.',
        defaultKeywords: 'video downloader, free video download',
        defaultImage: '/images/og-default.jpg',
        twitterHandle: '@downloadmod',
        themeColor: '#3b82f6',
        language: 'en',
        locale: 'en_US'
      };
    }
  }, [t]);

  // Get page-specific SEO configuration
  const pageSeo = useMemo(() => {
    // Special handling for home page: use specialCases.home from seo-templates.json
    if (pageKey === 'home' && seoTemplates?.specialCases?.home) {
      const home = seoTemplates.specialCases.home;
      return {
        title: home.title,
        description: home.description,
        keywords: home.keywords,
        image: home.image,
        schema: home.schema
      };
    }

    if (!pageKey) {
      return {
        title: defaultSeo.defaultTitle,
        description: defaultSeo.defaultDescription,
        keywords: defaultSeo.defaultKeywords,
        image: defaultSeo.defaultImage
      };
    }

    // For all other pages, only use t() if the key exists in the translation resource
    // If not, fallback to defaultSeo
    const titleKey = `pages.${pageKey}.title`;
    const descKey = `pages.${pageKey}.description`;
    const keywordsKey = `pages.${pageKey}.keywords`;
    const imageKey = `pages.${pageKey}.image`;
    const schemaKey = `pages.${pageKey}.schema`;

    // If t() returns the key itself, treat as missing and fallback
    const title = t(titleKey);
    const description = t(descKey);
    const keywords = t(keywordsKey);
    const image = t(imageKey);
    const schema = t(schemaKey, { returnObjects: true });

    if (
      title === titleKey ||
      description === descKey ||
      keywords === keywordsKey ||
      image === imageKey
    ) {
      return {
        title: defaultSeo.defaultTitle,
        description: defaultSeo.defaultDescription,
        keywords: defaultSeo.defaultKeywords,
        image: defaultSeo.defaultImage
      };
    }

    return {
      title,
      description,
      keywords,
      image,
      schema
    };
  }, [t, pageKey, defaultSeo, seoTemplates]);

  // Generate current URL
  const currentUrl = useMemo(() => {
    return `${defaultSeo.siteUrl}${router.asPath}`;
  }, [defaultSeo.siteUrl, router.asPath]);

  // Create SEO configuration
  const seoConfig: SeoConfig = useMemo(() => ({
    title: pageSeo.title,
    description: pageSeo.description,
    keywords: pageSeo.keywords,
    image: pageSeo.image,
    url: currentUrl,
    schema: pageSeo.schema
  }), [pageSeo, currentUrl]);

  // Generate meta tags
  const generateMetaTags = (customConfig?: Partial<SeoConfig>): SeoMetaTags => {
    const config = { ...seoConfig, ...customConfig };
    
    // Hreflang logic - Generate for all supported locales from i18n config
    // Get current path without locale prefix
    const currentPath = router.asPath;
    
    const pathWithoutLocale = router.locales?.reduce(
      (path, locale) => path.replace(new RegExp(`^/${locale}(\/|$)`), '/'),
      currentPath
    ) || currentPath;
    
    // Check if this is a platform page and get the platform ID
    const isPlatformPage = pathWithoutLocale.startsWith('/');
    let platformId = '';
    
    // Try to identify the platform from the path
    if (isPlatformPage) {
      // Extract platform ID from path (remove leading slash)
      const pathSegments = pathWithoutLocale.split('/').filter(Boolean);
      if (pathSegments.length > 0) {
        // Check if this is a known platform slug
        const slug = pathSegments[0];
        // Find platform ID from slug (reverse lookup)
        for (const [id, platformSlug] of Object.entries(platformSlugs)) {
          if (platformSlug === slug || id === slug) {
            platformId = id;
            break;
          }
        }
        
        // If no match found, use the slug as is
        if (!platformId) {
          platformId = slug;
        }
      }
    }
    
    // Base URL without trailing slash
    const baseUrl = defaultSeo.siteUrl.replace(/\/$/, '');
    
    // Current full URL (for canonical)
    const currentFullUrl = `${baseUrl}${currentPath}`;
    
    // Generate hreflang entries for all supported locales
    const hreflangs = router.locales?.map(localeCode => {
      let path;
      
      // If this is a platform page, use the platform slug
      if (isPlatformPage && platformId) {
        const slug = platformSlugs[platformId] || platformId;
        path = `/${slug}`;
      } else {
        // For other pages, use the original path
        path = pathWithoutLocale;
      }
      
      const href = localeCode === router.defaultLocale
        ? `${baseUrl}${path}`
        : `${baseUrl}/${localeCode}${path === '/' ? '' : path}`;
        
      return {
        hreflang: localeCode,
        href
      };
    }) || [];
    
    // Add x-default (points to default locale version)
    let xDefaultPath;
    if (isPlatformPage && platformId) {
      const slug = platformSlugs[platformId] || platformId;
      xDefaultPath = `/${slug}`;
    } else {
      xDefaultPath = pathWithoutLocale;
    }
    
    const xDefaultHref = `${baseUrl}${xDefaultPath}`;
    hreflangs.push({
      hreflang: 'x-default',
      href: xDefaultHref
    });

    return {
      title: config.title,
      description: config.description,
      keywords: config.keywords,
      canonical: currentFullUrl, // Use current full URL for canonical instead of default URL
      openGraph: {
        title: config.title,
        description: config.description,
        image: config.image.startsWith('http') ? config.image : `${defaultSeo.siteUrl}${config.image}`,
        url: config.url,
        type: 'website',
        siteName: defaultSeo.siteName,
        locale: defaultSeo.locale
      },
      twitter: {
        card: 'summary_large_image',
        title: config.title,
        description: config.description,
        image: config.image.startsWith('http') ? config.image : `${defaultSeo.siteUrl}${config.image}`,
        site: defaultSeo.twitterHandle
      },
      additionalTags: [
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'robots', content: 'index, follow' },
        { name: 'googlebot', content: 'index, follow' },
        { name: 'author', content: 'DownloadMod Team' },
        { name: 'theme-color', content: defaultSeo.themeColor },
        { name: 'msapplication-TileColor', content: defaultSeo.themeColor }
      ],
      jsonLd: config.schema,
      hreflangs
    };
  };

  const metaTags = useMemo(() => generateMetaTags(), [seoConfig, defaultSeo]);

  return {
    seoConfig,
    metaTags,
    generateMetaTags
  };
}

/**
 * Hook for platform-specific SEO
 */
export function usePlatformSeo(platform: string) {
  return useSeo(platform);
}

/**
 * Hook for homepage SEO
 */
export function useHomeSeo() {
  return useSeo('home');
}

export default useSeo;
